<template>
    <div class="p-4 mx-auto">
        <form @submit.prevent="handleSubmit" class="px-4">
            <div class="row">
                <div class="col-12 col-md-4 p-2 pr-md-3 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('btm.from') }}
                    </div>

                    <input
                        type="text"
                        name="from"
                        class="form-control pretty-input"
                        :placeholder="$t('btm.from')"
                        v-model="routeInfo.start_place"
                    >
                </div>

                <div class="col-12 col-md-4 p-2 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('btm.to') }}
                    </div>

                    <input
                        type="text"
                        name="to"
                        class="form-control pretty-input"
                        :placeholder="$t('btm.to')"
                        v-model="routeInfo.end_place"
                    >
                </div>

                <div class="col-12 col-md-4 p-2 pl-md-3 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('passengers') }}
                    </div>

                    <input class="form-control pretty-input" type="number" min="0" step="1" v-model="routeInfo.passengers_number">
                </div>
            </div>

            <div class="row">
                <div class="col-12 p-2 form-column">
                    <div class="form-check">
                        <input id="one-way" type="checkbox" class="form-check-input" v-model="routeInfo.one_way">

                        <label class="form-check-label text-sm font-weight-bold" for="one-way">
                            {{ $t('btm.one_way') }}
                        </label>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12 col-md-6 p-2 pr-3 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('departure_date') }}
                    </div>

                    <date-picker
                        value-type="YYYY-MM-DD"
                        format="dd DD MMM"
                        input-class="form-control pretty-input"
                        :placeholder="$t('btm.date_pick')"
                        style="border:none"
                        :disabled-date="setDepartureDateRange"
                        v-model="routeInfo.departure_date"
                        @clear="clearReturnDate"
                        :lang="datePickerLang"
                    />
                </div>

                <div class="col-12 col-md-6 p-2 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('departure_time') }}
                    </div>

                    <vue-timepicker
                        :placeholder="$t('btm.time_pick')"
                        input-class="form-control pretty-input"
                        :minute-interval="5"
                        v-model="routeInfo.departure_time"
                    />
                </div>
            </div>

            <div class="row" v-if="!routeInfo.one_way">
                <div class="col-12 col-md-6 p-2 pr-3 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('return_date') }}
                    </div>

                    <date-picker
                        value-type="YYYY-MM-DD"
                        format="dd DD MMM"
                        input-class="form-control pretty-input"
                        :placeholder="$t('btm.date_pick')"
                        style="border:none"
                        :disabled-date="setReturnDateRange"
                        :disabled="!routeInfo.departure_date || routeInfo.one_way"
                        v-model="routeInfo.return_date"
                        :lang="datePickerLang"
                    />
                </div>

                <div class="col-12 col-md-6 p-2 form-column">
                    <div class="text-sm font-weight-bold">
                        {{ $t('return_time') }}
                    </div>

                    <vue-timepicker
                        :placeholder="$t('btm.time_pick')"
                        input-class="form-control pretty-input"
                        :minute-interval="5"
                        :disabled="routeInfo.one_way"
                        v-model="routeInfo.return_time"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col-12 px-2 mt-3">
                    <button type="submit" class="btn btn-primary py-2 px-4">
                        {{ $t('next') }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
    import DatePicker from 'vue2-datepicker';
    import VueTimepicker from 'vue2-timepicker';
    import 'vue2-datepicker/index.css';
    import toastr from 'toastr';
    import {mapGetters} from 'vuex';
    import plLang from 'vue2-datepicker/locale/pl';
    import enLang from 'vue2-datepicker/locale/en';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    export default {
        components: {
            DatePicker,
            VueTimepicker,
        },

        data() {
            return {
                currentLanguage: window.APP.locale,
                startPlaces: [],
                endPlaces: [],

                localRouteInfo: {
                    start_place: null,
                    end_place: null,
                    departure_date: null,
                    departure_time: '',
                    return_date: null,
                    return_time: '',
                    one_way: false,
                    passengers_number: 1,
                },
            };
        },

        computed: {
            ...mapGetters({
                routeInfo: 'businessTravelManagement/getRouteInfo',
            }),

            datePickerLang() {
                if (this.currentLanguage === 'pl') {
                    return plLang;
                }

                return enLang;
            },
        },

        created() {
            this.localRouteInfo = this.routeInfo;
        },

        methods: {
            handleSubmit() {
                if (!this.localRouteInfo.start_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));

                    return;
                }

                if (!this.localRouteInfo.end_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('to')}));

                    return;
                }

                if (!this.localRouteInfo.departure_date) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('departure_date')}));

                    return;
                }

                if (!this.localRouteInfo.departure_time) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('departure_time')}));

                    return;
                } else {
                    let separatedTime = this.localRouteInfo.departure_time.split(':');
                    for (let n in separatedTime) {
                        if (isNaN(separatedTime[n])) {
                            toastr.error(this.$t('field_is_required', {field: this.$t('departure_time')}));

                            return;
                        }
                    }

                    let now = Date.now();
                    let formDepartureDate = Date.parse(this.localRouteInfo.departure_date + ' ' + this.localRouteInfo.departure_time);

                    if (formDepartureDate < now) {
                        toastr.error(this.$t('datetime_has_to_be_in_the_future', {field: this.$t('departure_time')}));

                        return;
                    }
                }

                if(!this.localRouteInfo.one_way) {
                    if (!this.localRouteInfo.return_date) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('return_date')}));

                        return;
                    }

                    if (!this.localRouteInfo.return_time) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('return_time')}));

                        return;
                    } else {
                        let separatedTime = this.localRouteInfo.return_time.split(':');
                        for (let n in separatedTime) {
                            if (isNaN(separatedTime[n])) {
                                toastr.error(this.$t('field_is_required', {field: this.$t('return_time')}));

                                return;
                            }
                        }

                        let now = Date.parse(this.localRouteInfo.departure_date + ' ' + this.localRouteInfo.departure_time);
                        let formDepartureDate = Date.parse(this.localRouteInfo.return_date + ' ' + this.localRouteInfo.return_time);

                        if (formDepartureDate < now) {
                            toastr.error(this.$t('time_has_to_be_later_than', {field: this.$t('return_time'), field_parent: this.$t('departure_time')}));

                            return;
                        }
                    }
                }

                if (this.localRouteInfo.passengers_number < 1) {
                    toastr.error(this.$t('validation.at_least_one_passenger_required'));

                    return;
                }

                this.$emit('setRoute', {
                    ...this.localRouteInfo,
                });
            },

            resetReturnValues() {
                this.localRouteInfo.return_date = null;
                this.localRouteInfo.return_time = "";
            },

            setDepartureDateRange(date) {
                let endDate = new Date();
                endDate.setDate(today.getDate() + 512);

                if (this.localRouteInfo.return_date) {
                    let return_date = new Date(this.localRouteInfo.return_date);
                    return date > return_date || date < today;
                }

                return date < today || date > endDate;
            },

            setReturnDateRange(date) {
                let endDate = new Date();
                endDate.setDate(today.getDate() + 512);

                if (this.localRouteInfo.departure_date) {
                    let departure_date = new Date(this.localRouteInfo.departure_date);
                    departure_date.setDate(departure_date.getDate() - 1);
                    return date < departure_date || date > endDate;
                }
                return date < today || date > endDate;
            },

            clearReturnDate() {
                this.localRouteInfo.return_date = null;
            },
        },
    };
</script>

<style scoped>
    .vue__time-picker {
        width: 100%;
    }
    .vue__time-picker >>> input.display-time {
        width: 100%;
    }
    .vue__time-picker >>> input.display-time:focus {
        outline: none;
    }
    .mx-datepicker {
        width: 100% !important;
    }
    .mx-datepicker >>> .mx-input-wrapper input {
        height: 36px;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-icon-calendar {
        display: none;
    }
    input::placeholder {
        font-size: 16px;
        color: #5f6a70;
    }
    .card * {
        color: #333333;
    }

    button {
        color: #ffffff !important;
        font-size: 1.2rem;
    }
</style>
